import { useMemo, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CircularProgress, Typography } from '@material-ui/core'
import { useHistory, useLocation, useParams } from 'react-router'
import { Check, Close } from '@material-ui/icons'

import { useGetKnowledgeBaseEntityQuery } from 'api/queries/knowledgeBase/knowledgeBase'
import { RestrictedContent } from 'components/auth/RestrictedContent/RestrictedContent'
import { DifficultySlider } from 'components/data/DifficultySlider/DifficultySlider'
import { QuestionList } from 'components/data/QuestionList/QuestionList'
import { ADMIN_ROLE, COMPANY_ROLE, CONTENT_CREATOR_ROLE, TEACHER_ROLE } from 'constants/roleGroups'
import { useDestination } from 'hooks/useDestination'
import { useIsMobile } from 'hooks/useIsMobile'
import { useCompanyId } from 'providers/CompanyProvider'
import { KnowledgeBaseContentType } from 'types/KnowledgeBaseContentType'
import { LabelChip } from 'components/data/LabelChip/LabelChip'
import { getKnowledgeBaseDisplayContentType, getMimeTypeFromDownloadFilePath, metadataRecordToMetadataEntries } from 'screens/KnowledgeBase/utils'
import { KnowledgeBaseDisplayContentType } from 'types/KnowledgeBaseDisplayContentType'
import * as Styled from '../knowledgeBase.styles'

interface ParamTypes {
  id: string
}

const KnowledgeBaseContentScreen = (): JSX.Element => {
  const { t, i18n: { language: userLanguageBrowserCode } } = useTranslation()
  const { id } = useParams<ParamTypes>()
  const isMobile = useIsMobile()
  const history = useHistory()
  const { pathname } = useLocation()
  const companyId = useCompanyId() || 0
  const didMount = useRef(false)

  const {
    data: knowledgeBaseData,
    isLoading,
    isError,
  } = useGetKnowledgeBaseEntityQuery(+id)

  const destination = useDestination(knowledgeBaseData)

  const handleEdit = () => {
    history.push(`${pathname}/edit`)
  }

  useEffect(() => {
    if (didMount.current) {
      history.replace('/knowledge-base')
    } else {
      didMount.current = true
    }
  }, [companyId, history])

  const imageLink = useMemo(() => (
    knowledgeBaseData?.image?.miniaturePath
  ), [knowledgeBaseData])

  const mimeType = getMimeTypeFromDownloadFilePath(knowledgeBaseData?.downloadFilePath || '')
  const contentFileType = getKnowledgeBaseDisplayContentType(mimeType)
  const isViewable = contentFileType !== KnowledgeBaseDisplayContentType.UNSUPPORTED

  const handleDisplayFile = () => {
    history.push(`${pathname}/display`)
  }

  const metadataEntries = useMemo(
    () => metadataRecordToMetadataEntries(knowledgeBaseData?.metadata),
    [knowledgeBaseData?.metadata]
  )

  return (
    <Styled.RootContainer>
      <Styled.HeaderContainer>
        <Typography variant='h2'>
          {t('knowledgeBase.content')}
        </Typography>
        <RestrictedContent accessRoles={[CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}>
          <Styled.EditButton $isMobile={isMobile} onClick={handleEdit}>
            {t('common.edit')}
          </Styled.EditButton>
        </RestrictedContent>
      </Styled.HeaderContainer>
      {isError || isLoading ? (
        <Styled.HelpersContainer>
          {isError && <Typography color='error'>{t('common.errors.generic')}</Typography>}
          {isLoading && <CircularProgress color='primary' />}
        </Styled.HelpersContainer>
      ) : (
        <Styled.ContentContainer>
          <Styled.InfoSection
            label={t('knowledgeBase.destination')}
            value={destination}
          />
          <RestrictedContent
            accessRoles={[TEACHER_ROLE, CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}
          >
            <Styled.InfoSection
              label={t('common.type')}
              value={t(`group.${[knowledgeBaseData?.dataType]}`)}
            />
          </RestrictedContent>
          <RestrictedContent
            accessRoles={[TEACHER_ROLE, CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}
          >
            <Styled.InfoSection
              label={t('common.visibility')}
              value={t(`knowledgeBase.visibility.${[knowledgeBaseData?.visibility]}`)}
            />
          </RestrictedContent>
          <Styled.InfoSection
            label={t('common.name')}
            value={
              knowledgeBaseData?.nameTranslations.find(
                ({ languageCode }) => languageCode === userLanguageBrowserCode
              )?.value || knowledgeBaseData?.name
            }
          />
          {knowledgeBaseData?.description && (
            <Styled.InfoSection
              label={t('common.description')}
              value={
                knowledgeBaseData?.descriptionTranslations?.find(
                  ({ languageCode }) => languageCode === userLanguageBrowserCode
                )?.value || knowledgeBaseData?.description
              }
            />
          )}
          <RestrictedContent
            accessRoles={[TEACHER_ROLE, CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}
          >
            <Styled.InfoSection
              label={t('knowledgeBase.photo')}
              value={!imageLink ? t('knowledgeBase.emptyPhoto') : ''}
            >
              {imageLink && (
                <Styled.ImagePreview imageSrc={imageLink} />
              )}
            </Styled.InfoSection>
            {knowledgeBaseData?.difficulty && (
              <Styled.InfoSection
                label={t('knowledgeBase.difficulty')}
                value={
                  <Styled.DifficultySliderContainer>
                    <DifficultySlider
                      name='difficulty'
                      value={knowledgeBaseData?.difficulty || 3}
                      track={false}
                      disabled
                    />
                  </Styled.DifficultySliderContainer>
              }
              />
            )}
            {knowledgeBaseData?.labels && (knowledgeBaseData?.labels.length || 0) > 0 && (
              <Styled.InfoSection
                label={t('common.labels')}
                value={
                  <Styled.LabelContainer>
                    {knowledgeBaseData?.labels
                      .sort((a, b) => a.id - b.id)
                      .map(({ id: labelId, color, name, nameTranslations }) => (
                        <LabelChip
                          key={labelId}
                          color={color}
                          name={name}
                          nameTranslations={nameTranslations}
                        />
                      ))}
                  </Styled.LabelContainer>
              }
              />
            )}
            {knowledgeBaseData?.duration && (
              <Styled.InfoSection
                label={t('knowledgeBase.duration')}
                value={`${(knowledgeBaseData?.duration || 0) / 60} ${t('common.minutes')}`}
              />
            )}
            {(knowledgeBaseData?.dataType === KnowledgeBaseContentType.PRESENTATION
              || knowledgeBaseData?.dataType === KnowledgeBaseContentType.TEST) && (
              <>
                {metadataEntries.length > 0 && (
                  <Styled.MetadataHeader>
                    <Typography variant='h3'>
                      {t('knowledgeBase.metadata')}
                    </Typography>
                  </Styled.MetadataHeader>
                )}
                {metadataEntries.map(
                  ({ key, value }) => (
                    <Styled.InfoSection key={key} label={t(key)} value={value} />
                  )
                )}
              </>
            )}
            {knowledgeBaseData?.dataType === KnowledgeBaseContentType.QUIZ && (
              <>
                <Styled.CheckboxSection>
                  <Styled.Checkbox
                    disabled
                    checked={knowledgeBaseData.canGoBack}
                    label={t('knowledgeBase.canGoBack')}
                    icon={<Close color='disabled' />}
                    checkedIcon={<Check color='primary' />}
                    labelFontSize='18px'
                    labelFontWeight='bold'
                  />
                  <Styled.Checkbox
                    disabled
                    checked={knowledgeBaseData.showAnswers}
                    label={t('knowledgeBase.showAnswers')}
                    icon={<Close color='disabled' />}
                    checkedIcon={<Check color='primary' />}
                    labelFontSize='18px'
                    labelFontWeight='bold'
                  />
                  <Styled.Checkbox
                    disabled
                    checked={knowledgeBaseData.randomQuestionOrder}
                    label={t('knowledgeBase.randomQuestionOrder')}
                    icon={<Close color='disabled' />}
                    checkedIcon={<Check color='primary' />}
                    labelFontSize='18px'
                    labelFontWeight='bold'
                  />
                </Styled.CheckboxSection>
                <RestrictedContent
                  accessRoles={[TEACHER_ROLE, CONTENT_CREATOR_ROLE, COMPANY_ROLE, ADMIN_ROLE]}
                >
                  <Styled.InfoSection
                    label={t('knowledgeBase.questionPool')}
                    value={
                      knowledgeBaseData.questions?.length
                        ? ''
                        : t('knowledgeBase.noQuestions')
                    }
                    editValueComponent={knowledgeBaseData.questions?.length && (
                      <QuestionList
                        questions={knowledgeBaseData.questions}
                        isDisplayOnly
                      />
                    )}
                  />
                </RestrictedContent>
              </>
            )}
          </RestrictedContent>
          {knowledgeBaseData?.dataType === KnowledgeBaseContentType.FILE && (
            <>
              {isViewable && (
                <Styled.DisplayButton onClick={() => handleDisplayFile()}>
                  {t('knowledgeBase.displayFile')}
                </Styled.DisplayButton>
              )}
              <Styled.DisplayButton>
                <Styled.DownloadLink to={knowledgeBaseData?.downloadFilePath || ''} target='_blank'>
                  {t('knowledgeBase.downloadFile')}
                </Styled.DownloadLink>
              </Styled.DisplayButton>
            </>
          )}
        </Styled.ContentContainer>
      )}
    </Styled.RootContainer>
  )
}

export { KnowledgeBaseContentScreen }
